import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PillTabs from 'components/shared/tabs/PillTabs';
import logger from 'services/logger';
import routes from 'common/routes';
import { premiumButtonSx } from 'common/theme/button/premiumButtonSx';
import {
  CREATE_ORGANIZATION_MANAGER,
  ORG_MANAGER_CANDIDATES,
  ORG_MANAGER_ORGANIZATIONS,
  SET_ORGANIZATION_MANAGERS
} from 'api/graphql/orgManager';

const EASE = 'cubic-bezier(0.32, 0.72, 0, 1)';
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const EMPTY_FORM = { first_name: '', last_name: '', email: '', phone: '' };

// Los inputs del modal comparten la misma superficie hundida y el foco dorado del sistema.
const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 2,
    backgroundColor: 'surface.content',
    '& fieldset': { borderColor: 'hairline.strong' },
    '&:hover fieldset': { borderColor: 'hairline.strong' },
    '&.Mui-focused fieldset': { borderColor: 'gold.main' }
  }
};

const labelSx = {
  display: 'block',
  mb: 0.75,
  fontWeight: 700,
  color: 'text.secondary',
  fontSize: '0.75rem',
  letterSpacing: 0.3
};

function OrgManagerCreateModal({ open, onClose }) {
  const { t } = useTranslation(['organizations', 'buttons']);

  const [tab, setTab] = useState(0);
  const [organizationId, setOrganizationId] = useState('');
  const [member, setMember] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [sendAccess, setSendAccess] = useState(true);
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [result, setResult] = useState(null);
  // Se creó al menos un gestor en esta apertura del modal. Sobrevive a "Crear otro", que sí
  // limpia el formulario: si no, cerrar tras encadenar altas dejaría la lista desactualizada.
  const [dirty, setDirty] = useState(false);

  const { data: orgsData, loading: orgsLoading } = useQuery(ORG_MANAGER_ORGANIZATIONS, {
    skip: !open,
    fetchPolicy: 'cache-and-network'
  });
  const organizations = useMemo(
    () => (orgsData?.organizations?.data ?? []).filter((o) => !o.is_default),
    [orgsData]
  );

  const { data: candidatesData, loading: candidatesLoading } = useQuery(ORG_MANAGER_CANDIDATES, {
    variables: { organization_id: [Number(organizationId)] },
    skip: !open || tab !== 0 || !organizationId,
    fetchPolicy: 'cache-and-network'
  });
  // Quien ya es gestor no vuelve a la lista: designarlo otra vez no haría nada.
  const candidates = useMemo(
    () => (candidatesData?.users?.data ?? []).filter((u) => !u.is_org_manager),
    [candidatesData]
  );

  const [setManagers, { loading: designating }] = useMutation(SET_ORGANIZATION_MANAGERS);
  const [createManager, { loading: creating }] = useMutation(CREATE_ORGANIZATION_MANAGER);
  const submitting = designating || creating;

  // Cambiar de empresa invalida la persona elegida: pertenecía a la lista anterior.
  useEffect(() => {
    setMember(null);
  }, [organizationId]);

  const resetForm = () => {
    setTab(0);
    setOrganizationId('');
    setMember(null);
    setForm(EMPTY_FORM);
    setSendAccess(true);
    setFieldErrors({});
    setSubmitError('');
    setResult(null);
  };

  const handleClose = () => {
    resetForm();
    setDirty(false);
    onClose(dirty);
  };

  const selectedOrg = organizations.find((o) => String(o.id) === String(organizationId));

  const validateNewUser = () => {
    const errors = {};
    if (!organizationId) errors.organization = t('managerOrgRequired', { defaultValue: 'Elige la empresa.' });
    if (!form.first_name.trim()) errors.first_name = t('managerNameRequired', { defaultValue: 'El nombre es obligatorio.' });
    if (!form.last_name.trim()) errors.last_name = t('managerLastNameRequired', { defaultValue: 'El apellido es obligatorio.' });
    if (!EMAIL_RE.test(form.email.trim())) errors.email = t('managerEmailInvalid', { defaultValue: 'Escribe un correo válido.' });
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const submitDesignate = async () => {
    setSubmitError('');
    if (!organizationId || !member) {
      setFieldErrors({
        organization: !organizationId ? t('managerOrgRequired', { defaultValue: 'Elige la empresa.' }) : undefined,
        member: !member ? t('managerMemberRequired', { defaultValue: 'Elige a la persona.' }) : undefined
      });
      return;
    }
    try {
      await setManagers({
        variables: { organization_id: organizationId, user_ids: [member.id], is_manager: true }
      });
      setResult({
        name: member.full_name,
        email: member.email,
        organizationName: selectedOrg?.name || '',
        isNew: false,
        accessSent: false
      });
      setDirty(true);
    } catch (error) {
      logger.error('setOrganizationManagers failed', error, { organizationId, userId: member.id });
      setSubmitError(t('managerSaveError', { defaultValue: 'No se pudo guardar. Inténtalo de nuevo.' }));
    }
  };

  const submitCreate = async () => {
    setSubmitError('');
    if (!validateNewUser()) return;
    try {
      const { data } = await createManager({
        variables: {
          organization_id: organizationId,
          input: {
            first_name: form.first_name.trim(),
            last_name: form.last_name.trim(),
            email: form.email.trim(),
            phone: form.phone.trim() || null,
            send_access: sendAccess
          }
        }
      });
      const created = data?.createOrganizationManager;
      setResult({
        name: created?.full_name || `${form.first_name} ${form.last_name}`.trim(),
        email: created?.email || form.email.trim(),
        organizationName: created?.organization?.name || selectedOrg?.name || '',
        isNew: true,
        accessSent: sendAccess
      });
      setDirty(true);
    } catch (error) {
      logger.error('createOrganizationManager failed', error, { organizationId, email: form.email });
      setSubmitError(t('managerSaveError', { defaultValue: 'No se pudo guardar. Inténtalo de nuevo.' }));
    }
  };

  const setField = (key) => (event) => {
    const { value } = event.target;
    setForm((prev) => ({ ...prev, [key]: value }));
    setFieldErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const orgSelect = (errorKey) => (
    <Box>
      <Typography component="label" htmlFor="org-manager-company" sx={labelSx}>
        {t('managerCompany', { defaultValue: 'Empresa' })}
      </Typography>
      <TextField
        id="org-manager-company"
        select
        fullWidth
        size="small"
        value={organizationId}
        onChange={(e) => {
          setOrganizationId(e.target.value);
          setFieldErrors((prev) => ({ ...prev, organization: undefined }));
        }}
        disabled={orgsLoading}
        error={Boolean(fieldErrors[errorKey])}
        helperText={fieldErrors[errorKey] || ''}
        sx={fieldSx}>
        {organizations.length === 0 && (
          <MenuItem value="" disabled>
            {orgsLoading
              ? t('managerLoading', { defaultValue: 'Cargando...' })
              : t('managerNoCompanies', { defaultValue: 'Aún no hay empresas creadas.' })}
          </MenuItem>
        )}
        {organizations.map((o) => (
          <MenuItem key={o.id} value={o.id}>
            {o.name}
          </MenuItem>
        ))}
      </TextField>
    </Box>
  );

  const designatePanel = (
    <Stack spacing={2.5}>
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        {t('managerDesignateHelp', {
          defaultValue:
            'Asciende a alguien que ya está en la nómina de la empresa. Conserva su cuenta y suma el panel de bienestar.'
        })}
      </Typography>
      {orgSelect('organization')}
      <Box>
        <Typography component="label" htmlFor="org-manager-person" sx={labelSx}>
          {t('managerPerson', { defaultValue: 'Persona' })}
        </Typography>
        <Autocomplete
          id="org-manager-person"
          options={candidates}
          value={member}
          onChange={(_, value) => {
            setMember(value);
            setFieldErrors((prev) => ({ ...prev, member: undefined }));
          }}
          disabled={!organizationId || candidatesLoading}
          getOptionLabel={(option) => option.full_name || option.email || ''}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          noOptionsText={t('managerNoCandidates', {
            defaultValue: 'Esta empresa no tiene miembros disponibles.'
          })}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              <Stack sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                  {option.full_name}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap>
                  {option.email}
                </Typography>
              </Stack>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              placeholder={
                organizationId
                  ? t('managerPersonPlaceholder', { defaultValue: 'Buscar por nombre o correo' })
                  : t('managerPickCompanyFirst', { defaultValue: 'Elige primero la empresa' })
              }
              error={Boolean(fieldErrors.member)}
              helperText={fieldErrors.member || ''}
              sx={fieldSx}
            />
          )}
        />
      </Box>
    </Stack>
  );

  const createPanel = (
    <Stack spacing={2.5}>
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        {t('managerCreateHelp', {
          defaultValue:
            'Para la persona de RRHH que no está en la nómina de colaboradores. Se crea su cuenta, se vincula a la empresa y se le da el panel.'
        })}
      </Typography>
      {orgSelect('organization')}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <Box sx={{ flex: 1 }}>
          <Typography component="label" htmlFor="org-manager-first-name" sx={labelSx}>
            {t('managerFirstName', { defaultValue: 'Nombre' })}
          </Typography>
          <TextField
            id="org-manager-first-name"
            fullWidth
            size="small"
            value={form.first_name}
            onChange={setField('first_name')}
            error={Boolean(fieldErrors.first_name)}
            helperText={fieldErrors.first_name || ''}
            sx={fieldSx}
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography component="label" htmlFor="org-manager-last-name" sx={labelSx}>
            {t('managerLastName', { defaultValue: 'Apellido' })}
          </Typography>
          <TextField
            id="org-manager-last-name"
            fullWidth
            size="small"
            value={form.last_name}
            onChange={setField('last_name')}
            error={Boolean(fieldErrors.last_name)}
            helperText={fieldErrors.last_name || ''}
            sx={fieldSx}
          />
        </Box>
      </Stack>
      <Box>
        <Typography component="label" htmlFor="org-manager-email" sx={labelSx}>
          {t('managerEmail', { defaultValue: 'Correo' })}
        </Typography>
        <TextField
          id="org-manager-email"
          fullWidth
          size="small"
          type="email"
          value={form.email}
          onChange={setField('email')}
          error={Boolean(fieldErrors.email)}
          helperText={
            fieldErrors.email ||
            t('managerEmailHelp', { defaultValue: 'Con este correo inicia sesión y recibe su acceso.' })
          }
          sx={fieldSx}
        />
      </Box>
      <Box>
        <Typography component="label" htmlFor="org-manager-phone" sx={labelSx}>
          {t('managerPhone', { defaultValue: 'Teléfono (opcional)' })}
        </Typography>
        <TextField
          id="org-manager-phone"
          fullWidth
          size="small"
          value={form.phone}
          onChange={setField('phone')}
          sx={fieldSx}
        />
      </Box>
      <FormControlLabel
        control={
          <Checkbox
            checked={sendAccess}
            onChange={(e) => setSendAccess(e.target.checked)}
            sx={{ color: 'hairline.strong', '&.Mui-checked': { color: 'gold.main' } }}
          />
        }
        label={
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {t('managerSendAccess', {
              defaultValue: 'Enviarle ahora el correo con su acceso y el enlace para crear su contraseña'
            })}
          </Typography>
        }
      />
    </Stack>
  );

  const successPanel = result && (
    <Stack spacing={2.5} sx={{ textAlign: 'center', alignItems: 'center' }}>
      <CheckCircleOutlineIcon sx={{ fontSize: 48, color: 'sessionStatus.completed.fg' }} />
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 800 }}>
          {result.isNew
            ? t('managerCreatedTitle', { defaultValue: 'Gestor creado' })
            : t('managerDesignatedTitle', { defaultValue: 'Gestor designado' })}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
          {t('managerCreatedBody', {
            defaultValue: '{{name}} ya administra el bienestar de {{company}}.',
            name: result.name,
            company: result.organizationName
          })}
        </Typography>
      </Box>
      <Box
        sx={{
          width: '100%',
          textAlign: 'left',
          p: 2,
          borderRadius: 2.5,
          backgroundColor: 'surface.content',
          border: '1px solid',
          borderColor: 'hairline.main'
        }}>
        <Typography variant="caption" sx={{ ...labelSx, mb: 1 }}>
          {t('managerNextSteps', { defaultValue: 'Qué sigue' })}
        </Typography>
        <Stack component="ol" spacing={1} sx={{ m: 0, pl: 2.5 }}>
          <Typography component="li" variant="body2" sx={{ color: 'text.secondary' }}>
            {t('managerNextLogin', {
              defaultValue: 'Debe iniciar sesión con {{email}}: al entrar verá su panel en {{path}}.',
              email: result.email,
              path: routes.orgClientDashboard.path
            })}
          </Typography>
          {result.isNew && (
            <Typography component="li" variant="body2" sx={{ color: 'text.secondary' }}>
              {result.accessSent
                ? t('managerNextAccessSent', {
                    defaultValue:
                      'Le enviamos un correo con su contraseña temporal y el enlace para definir la suya.'
                  })
                : t('managerNextAccessPending', {
                    defaultValue:
                      'Todavía no tiene contraseña: envíale el acceso desde la ficha de la empresa, en Organizaciones.'
                  })}
            </Typography>
          )}
          <Typography component="li" variant="body2" sx={{ color: 'text.secondary' }}>
            {t('managerNextRelogin', {
              defaultValue:
                'Si ya tenía la sesión abierta, debe cerrarla y volver a entrar para que le aparezca el panel.'
            })}
          </Typography>
        </Stack>
      </Box>
    </Stack>
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          backgroundColor: 'surface.content',
          backgroundImage: 'none',
          border: '1px solid',
          borderColor: 'hairline.main'
        }
      }}>
      <DialogTitle sx={{ pt: 3, pb: 1, pr: 6 }}>
        <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: '-0.01em' }}>
          {result
            ? t('managerDoneTitle', { defaultValue: 'Listo' })
            : t('managerNew', { defaultValue: 'Nuevo gestor de bienestar' })}
        </Typography>
        <IconButton
          onClick={handleClose}
          size="small"
          aria-label={t('close', { ns: 'buttons', defaultValue: 'Cerrar' })}
          sx={{
            position: 'absolute',
            right: 12,
            top: 16,
            color: 'text.secondary',
            transition: `transform 200ms ${EASE}`,
            '&:hover': { color: 'text.primary' },
            '&:active': { transform: 'scale(0.98)' }
          }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pb: 1 }}>
        {result ? (
          successPanel
        ) : (
          <Stack spacing={2.5}>
            <PillTabs
              value={tab}
              onChange={(_, value) => {
                setTab(value);
                setFieldErrors({});
                setSubmitError('');
              }}
              fullWidth
              ariaLabel={t('managerTabsLabel', { defaultValue: 'Cómo crear el gestor' })}
              tabs={[
                { key: 'designate', label: t('managerTabDesignate', { defaultValue: 'Designar miembro' }) },
                { key: 'create', label: t('managerTabCreate', { defaultValue: 'Crear usuario' }) }
              ]}
            />
            {tab === 0 ? designatePanel : createPanel}
            {submitError && (
              <Alert severity="error" variant="outlined" sx={{ borderRadius: 2 }}>
                {submitError}
              </Alert>
            )}
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 2 }}>
        {result ? (
          <Stack direction={{ xs: 'column-reverse', sm: 'row' }} spacing={1.5} sx={{ width: '100%' }}>
            <Button
              onClick={resetForm}
              fullWidth
              variant="outlined"
              color="inherit"
              sx={{
                py: 1.2,
                borderRadius: 2,
                color: 'text.primary',
                borderColor: 'hairline.strong',
                fontWeight: 700,
                textTransform: 'none'
              }}>
              {t('managerAddAnother', { defaultValue: 'Crear otro' })}
            </Button>
            <Button onClick={handleClose} fullWidth sx={{ ...premiumButtonSx, py: 1.2 }}>
              {t('managerDone', { defaultValue: 'Entendido' })}
            </Button>
          </Stack>
        ) : (
          <Stack direction={{ xs: 'column-reverse', sm: 'row' }} spacing={1.5} sx={{ width: '100%' }}>
            <Button
              onClick={handleClose}
              fullWidth
              variant="outlined"
              color="inherit"
              sx={{
                py: 1.2,
                borderRadius: 2,
                color: 'text.primary',
                borderColor: 'hairline.strong',
                fontWeight: 700,
                textTransform: 'none'
              }}>
              {t('cancel', { ns: 'buttons', defaultValue: 'Cancelar' })}
            </Button>
            <Button
              onClick={tab === 0 ? submitDesignate : submitCreate}
              disabled={submitting}
              fullWidth
              sx={{ ...premiumButtonSx, py: 1.2 }}>
              {tab === 0
                ? t('managerDesignateCta', { defaultValue: 'Designar gestor' })
                : t('managerCreateCta', { defaultValue: 'Crear gestor' })}
            </Button>
          </Stack>
        )}
      </DialogActions>
    </Dialog>
  );
}

OrgManagerCreateModal.propTypes = {
  open: PropTypes.bool.isRequired,
  // Recibe `true` cuando en la sesión del modal se creó o designó al menos un gestor.
  onClose: PropTypes.func.isRequired
};

export default OrgManagerCreateModal;
